import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { CorrelationHeatmap } from "@/components/analytics/CorrelationHeatmap";
import { RelativeStrengthTable } from "@/components/analytics/RelativeStrengthTable";
import PageMeta from "@/components/PageMeta";

const TABS = [
  { id: "correlation", label: "Correlation Matrix", active: true },
  { id: "relative-strength", label: "Relative Strength", active: true },
  { id: "volatility", label: "Volatility Regime", active: false },
  { id: "sector-rotation", label: "Sector Rotation", active: false },
  { id: "funding", label: "Funding & Liquidation", active: false },
] as const;

type TabId = (typeof TABS)[number]["id"];

const MarketAnalytics = () => {
  const [tab, setTab] = useState<TabId>("correlation");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Market Analytics Terminal | GoToCoins"
        description="Institutional-grade market analysis for the top cryptocurrencies — correlation matrix, relative strength, volatility, sector rotation and funding data."
        path="/market-analytics"
      />

      {/* Terminal header */}
      <div className="border-b border-border/60 bg-card/30">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to GoToCoins
          </Link>
          <div className="inline-flex items-center gap-2 text-[11px] font-mono text-muted-foreground uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            Live
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8">
        <div className="flex items-start gap-3 mb-8">
          <BarChart3 className="w-8 h-8 text-primary shrink-0 mt-1" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              Market Analytics Terminal
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Institutional-grade market analysis for the top cryptocurrencies
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="border-b border-border/60 mb-8 overflow-x-auto">
          <div className="inline-flex gap-1 min-w-max">
            {TABS.map((t) => {
              const isActive = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => t.active && setTab(t.id)}
                  disabled={!t.active}
                  className={`px-4 py-2.5 text-xs font-mono uppercase tracking-wider border-b-2 transition-colors ${
                    isActive
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  } ${!t.active ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {t.label}
                  {!t.active && (
                    <span className="ml-2 text-[9px] px-1.5 py-0.5 border border-border/60 rounded">
                      SOON
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active panel */}
        {tab === "correlation" && <CorrelationHeatmap />}
        {tab === "relative-strength" && <RelativeStrengthTable />}


        {/* ASIC compliance footer */}
        <div className="mt-16 pt-6 border-t border-border/40">
          <p className="text-[11px] leading-relaxed text-muted-foreground max-w-4xl">
            Market Analytics data is generated algorithmically and is provided for educational and
            informational purposes only. It does not constitute financial advice, a recommendation,
            or an offer to buy or sell any cryptocurrency. Past correlations and patterns do not
            guarantee future results. Always conduct your own research before making investment
            decisions.
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default MarketAnalytics;
